/**
 * Consultas — Audio → Transcript → AI Analysis
 *
 * Tab 1 "Nova Consulta": drop-zone upload + live progress stepper
 * Tab 2 "Histórico": list of past consultas with expand + retry-analysis
 *
 * Story DEMO.6
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowClockwise,
  ArrowRight,
  ChartBar,
  CheckCircle,
  CircleNotch,
  CloudArrowUp,
  FileAudio,
  MicrophoneStage,
  ChartLineUp,
  Warning,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { friendlyError, toastError } from "@/lib/userMessages";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { AppLayout } from "@/components/layout/AppLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useConsultasAI } from "@/features/consultas/hooks/useConsultasAI";
import { useUploadConsulta } from "@/features/consultas/hooks/useUploadConsulta";
import { useAnalyzeTranscript } from "@/features/consultas/hooks/useAnalyzeTranscript";
import { MIN_TRANSCRIPT_CHARS } from "@/features/consultas/api/analyzeTranscript";
import { useDeleteConsulta } from "@/features/consultas/hooks/useDeleteConsulta";
import { useRenameConsulta } from "@/features/consultas/hooks/useRenameConsulta";
import { DeleteEntityButton } from "@/components/shared/DeleteEntityButton";
import { InlineEditText } from "@/components/shared/InlineEditText";
import { BulkActionsBar } from "@/components/shared/BulkActionsBar";
import { Checkbox } from "@/components/ui/checkbox";
import { useJobRealtime } from "@/features/consultas/hooks/useJobRealtime";
import type { ConsultaAI, JobStage } from "@/features/consultas/types";
import { useSession } from "@clerk/clerk-react";
import { useCreateNotification } from "@/features/notifications/hooks/useCreateNotification";
import { cn } from "@/lib/utils";
import { useHasTranscriptionKey } from "@/features/byok/hooks/useHasTranscriptionKey";
import { TranscriptionKeyGate } from "@/features/byok/components/TranscriptionKeyGate";
import { useRecordConsent } from "@/features/consent/hooks/useRecordConsent";
import { HelpButton } from "@/components/ui/help-button";

// ── Stepper labels ────────────────────────────────────────────────────────────

interface StepDef {
  stage: JobStage[];
  label: string;
}

const STEPS: StepDef[] = [
  { stage: ["hashing", "chunking"], label: "Preparando" },
  { stage: ["uploading"], label: "Enviando" },
  { stage: ["transcribing"], label: "Transcrevendo" },
  { stage: ["analyzing"], label: "Analisando" },
  { stage: ["done"], label: "Concluído" },
];

function stepIndex(stage: JobStage): number {
  for (let i = 0; i < STEPS.length; i++) {
    if (STEPS[i].stage.includes(stage)) return i;
  }
  return -1;
}

// ── Progress Stepper ──────────────────────────────────────────────────────────

function ProgressStepper({ stage }: { stage: JobStage }) {
  const current = stepIndex(stage);
  const isError = stage === "error";

  // A9-A11y — single live region announces only the current step name on
  // change, instead of the entire OL re-announcing every label on each
  // transition (WCAG 4.1.3 + 2.4.3).
  const liveText = (() => {
    if (isError) return `Etapa ${current + 1} de ${STEPS.length}: erro em ${STEPS[current]?.label ?? "—"}`;
    if (current < 0) return "";
    return `Etapa ${current + 1} de ${STEPS.length}: ${STEPS[current].label}`;
  })();

  return (
    <>
      <span role="status" aria-live="polite" className="sr-only">
        {liveText}
      </span>
    <ol
      className="flex items-start gap-2 sm:gap-4 mt-6"
      aria-label="Progresso da análise"
    >
      {STEPS.map((step, i) => {
        const done = current > i;
        const active = current === i && !isError;
        const errored = isError && current === i;

        return (
          <li key={step.label} className="flex flex-col items-center flex-1">
            <div
              className={[
                "flex items-center justify-center w-8 h-8 rounded-full border-2 transition-colors",
                done
                  ? "bg-primary border-primary text-primary-foreground"
                  : active
                    ? "border-primary text-primary animate-pulse"
                    : errored
                      ? "border-destructive text-destructive"
                      : "border-muted text-muted-foreground",
              ].join(" ")}
              aria-current={active ? "step" : undefined}
            >
              {done ? (
                <CheckCircle weight="bold" className="w-5 h-5" aria-hidden="true" />
              ) : active ? (
                <CircleNotch weight="bold" className="w-5 h-5 animate-spin" aria-hidden="true" />
              ) : errored ? (
                <Warning weight="bold" className="w-5 h-5" aria-hidden="true" />
              ) : (
                <span className="text-xs font-semibold">{i + 1}</span>
              )}
            </div>
            <span
              className={[
                "mt-1 text-xs text-center leading-tight",
                done || active ? "text-foreground font-medium" : "text-muted-foreground",
              ].join(" ")}
            >
              {step.label}
            </span>
            {i < STEPS.length - 1 && (
              <div
                className={[
                  "hidden sm:block absolute w-full h-0.5 top-4 left-1/2",
                  done ? "bg-primary" : "bg-muted",
                ].join(" ")}
                aria-hidden="true"
              />
            )}
          </li>
        );
      })}
    </ol>
    </>
  );
}

// ── Upload Drop Zone ──────────────────────────────────────────────────────────

interface DropZoneProps {
  onFile: (file: File) => void;
  disabled?: boolean;
  /** Optional reason rendered when disabled (e.g. consent missing). */
  disabledReason?: string;
}

const ACCEPTED_EXT = ".mp3,.wav,.m4a,.mp4,.ogg";

function DropZone({ onFile, disabled, disabledReason }: DropZoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      if (disabled) {
        if (disabledReason) toast.error(disabledReason);
        return;
      }
      const file = e.dataTransfer.files[0];
      if (file) onFile(file);
    },
    [onFile, disabled, disabledReason],
  );

  return (
    <>
      <input
        ref={inputRef}
        id="dropzone-file-input"
        type="file"
        accept={ACCEPTED_EXT}
        aria-label="Selecionar arquivo de áudio"
        className="sr-only"
        disabled={disabled}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onFile(file);
          e.target.value = "";
        }}
      />
      {/* A3-A11y — proper <button> instead of <label role="button">.
          Native button gets free keyboard semantics + screen reader role. */}
      <button
        type="button"
        disabled={disabled}
        aria-label={
          disabled && disabledReason
            ? disabledReason
            : "Arraste seu áudio aqui ou clique para selecionar"
        }
        aria-controls="dropzone-file-input"
        onClick={() => {
          if (!disabled) inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={[
          "relative flex flex-col items-center justify-center gap-4 rounded-xl border-2 border-dashed p-10 transition-colors w-full",
          dragOver
            ? "border-primary bg-primary/10 scale-[1.01]"
            : "border-border/60 hover:border-primary/60 hover:bg-primary/5",
          disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
        ].join(" ")}
      >
        <MicrophoneStage
          weight="duotone"
          className={cn(
            "w-12 h-12 transition-colors",
            dragOver ? "text-primary" : "text-primary/80",
          )}
          aria-hidden="true"
        />
        <div className="text-center">
          <p className="font-medium text-foreground">
            {disabled && disabledReason
              ? disabledReason
              : "Arraste seu áudio aqui ou clique para selecionar"}
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            MP3, WAV, M4A, MP4, OGG — máximo 100 MB
          </p>
        </div>
      </button>
    </>
  );
}

// ── Consent Gate (LGPD per-event) ─────────────────────────────────────────────

interface ConsentGateProps {
  id: string;
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  label: string;
  helper: string;
}

function ConsentGate({ id, checked, onCheckedChange, disabled, label, helper }: ConsentGateProps) {
  return (
    <div className="rounded-lg border border-border/60 bg-muted/30 p-3 sm:p-4">
      <div className="flex items-start gap-3">
        <Checkbox
          id={id}
          checked={checked}
          onCheckedChange={(v) => onCheckedChange(Boolean(v))}
          disabled={disabled}
          // Mobile WCAG 2.5.5 — minimum 48x48 hit area. Visual checkmark stays
          // ~20px (`size-5`) via inner indicator scaling, but the actual
          // interactive target meets the touch-target spec.
          className="mt-0.5 h-12 w-12 [&_svg]:h-5 [&_svg]:w-5"
          aria-describedby={`${id}-helper`}
        />
        <div className="flex-1 min-w-0">
          <label htmlFor={id} className="text-sm font-medium text-foreground leading-snug cursor-pointer block">
            {label}
          </label>
          <p id={`${id}-helper`} className="text-xs text-muted-foreground mt-1 leading-snug">
            {helper}{" "}
            <a
              href="/legal/privacidade"
              target="_blank"
              rel="noopener noreferrer"
              // WCAG 2.5.5 — render as block-level pill on mobile so the hit
              // area meets 48x48; inline visual treatment kept on sm+.
              className="mt-1 inline-flex min-h-[48px] min-w-[48px] items-center px-2 underline hover:text-foreground transition-colors sm:mt-0 sm:min-h-0 sm:min-w-0 sm:p-0"
            >
              ver política
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

// ── File selected preview ─────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extractAnalysisPreview(analysis: ConsultaAI["analysisPayload"]): string | null {
  if (!analysis || typeof analysis !== "object") return null;

  const directPreview =
    typeof analysis.resumo_executivo === "string"
      ? analysis.resumo_executivo
      : typeof analysis.queixa_principal === "string"
        ? analysis.queixa_principal
        : typeof analysis.mensagem === "string"
          ? analysis.mensagem
          : null;

  if (directPreview) return directPreview.slice(0, 220);

  const raw = typeof analysis.analysis === "string" ? analysis.analysis : "";
  if (!raw) return null;

  try {
    const cleaned = raw.replace(/^```(?:json)?\s*/i, "").replace(/```\s*$/i, "").trim();
    const parsed = JSON.parse(cleaned) as Record<string, unknown>;
    const preview =
      typeof parsed.resumo_executivo === "string"
        ? parsed.resumo_executivo
        : typeof parsed.queixa_principal === "string"
          ? parsed.queixa_principal
          : typeof parsed.mensagem === "string"
            ? parsed.mensagem
            : raw;
    return preview.slice(0, 220);
  } catch {
    return raw.slice(0, 220);
  }
}

// ── Status Badge ──────────────────────────────────────────────────────────────

function StatusBadge({
  status,
  failedStage,
  analysisPayload,
}: {
  status?: string;
  failedStage?: string | null;
  analysisPayload?: ConsultaAI["analysisPayload"];
}) {
  if (!status || status === "running" || status === "pending") {
    return (
      <Badge variant="secondary" className="gap-1">
        <CircleNotch className="w-3 h-3 animate-spin" aria-hidden="true" />
        Processando
      </Badge>
    );
  }
  // A11-UX: partial result — job completed but analysis embeds an error or
  // is missing the expected payload. User can still retry analysis.
  const hasAnalysisError =
    analysisPayload &&
    typeof analysisPayload === "object" &&
    "erro" in (analysisPayload as Record<string, unknown>);
  const isPartial =
    status === "done" && (hasAnalysisError || !analysisPayload);
  if (status === "done" && !isPartial) {
    return (
      <Badge className="bg-primary text-primary-foreground gap-1">
        <CheckCircle className="w-3 h-3" aria-hidden="true" />
        Concluída
      </Badge>
    );
  }
  if (isPartial) {
    return (
      <Badge className="bg-warning text-warning-foreground gap-1">
        <Warning className="w-3 h-3" aria-hidden="true" />
        Parcial
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1">
      <Warning className="w-3 h-3" aria-hidden="true" />
      {failedStage === "analysis" ? "Falhou (análise)" : "Falhou"}
    </Badge>
  );
}

// ── Consulta History Row ──────────────────────────────────────────────────────

interface ConsultaRowProps {
  consulta: ConsultaAI;
  onOpen: () => void;
  onDelete: () => Promise<void>;
  onRename: (newName: string) => Promise<void>;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelected: (checked: boolean) => void;
  disableActions?: boolean;
}

function ConsultaRow({
  consulta,
  onOpen,
  onDelete,
  onRename,
  selectionMode,
  selected,
  onToggleSelected,
  disableActions,
}: ConsultaRowProps) {
  return (
    <div
      role="button"
      tabIndex={0}
      className="w-full flex items-center justify-between p-4 text-left gap-3 rounded-lg border bg-card hover:bg-muted/50 transition-colors cursor-pointer"
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        {selectionMode ? (
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selected}
              onCheckedChange={(v) => onToggleSelected(Boolean(v))}
              aria-label="Selecionar consulta"
              disabled={disableActions}
            />
          </div>
        ) : null}
        <FileAudio
          weight="duotone"
          className="w-5 h-5 text-muted-foreground shrink-0"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <InlineEditText
            value={consulta.label}
            onSave={onRename}
            placeholder="Consulta sem nome"
            className="font-medium text-foreground text-sm"
            disabled={disableActions}
          />
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-0.5">
            <p className="text-xs text-muted-foreground">
              {new Date(consulta.createdAt).toLocaleString("pt-BR")}
            </p>
            <StatusBadge
              status={consulta.jobStatus}
              failedStage={consulta.jobFailedAtStage}
              analysisPayload={consulta.analysisPayload}
            />
          </div>
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <DeleteEntityButton
          entityLabel="consulta"
          confirmDescription="A consulta e sua análise serão removidas permanentemente."
          onConfirm={onDelete}
          disabled={disableActions}
          data-testid={`consulta-delete-${consulta.id}`}
        />
        <ArrowRight className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function Consultas() {
  usePageTitle("Analisar Consulta");

  const navigate = useNavigate();
  const [inputMode, setInputMode] = useState<"upload" | "paste">("upload");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [label, setLabel] = useState("");
  const [transcriptText, setTranscriptText] = useState("");
  const [latestJobId, setLatestJobId] = useState<string | null>(null);
  const [successPreview, setSuccessPreview] = useState<string | null>(null);
  // LGPD audio/transcript consent — per-event affirmative consent.
  // Reset to false after each successful submission so the next upload
  // is an explicit re-affirmation (LGPD Art. 7º + 11º audit trail).
  const [audioConsent, setAudioConsent] = useState(false);
  const [transcriptConsent, setTranscriptConsent] = useState(false);

  const { session: _session } = useSession();
  const transcriptionKey = useHasTranscriptionKey();
  const upload = useUploadConsulta();
  const analyzeTranscriptM = useAnalyzeTranscript();
  const recordConsent = useRecordConsent();
  const consultasQuery = useConsultasAI();
  const createNotification = useCreateNotification();
  const deleteConsulta = useDeleteConsulta();
  const renameConsulta = useRenameConsulta();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set((consultasQuery.data ?? []).map((c) => c.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [consultasQuery.data]);

  // Realtime: watch latest job
  useJobRealtime(latestJobId);

  // Also watch any in-progress jobs in history
  const inProgressJobId =
    consultasQuery.data?.find((c) => c.jobStatus === "running" || c.jobStatus === "pending")
      ?.aiJobId ?? null;
  useJobRealtime(inProgressJobId);

  const handleFile = (file: File) => {
    setSelectedFile(file);
    // Pre-fill label from filename (strip extension)
    const name = file.name.replace(/\.[^.]+$/, "").replace(/[_-]/g, " ");
    setLabel(name.length > 0 ? name : "Consulta");
    upload.reset();
    setSuccessPreview(null);
  };

  const handleStartUpload = () => {
    if (!selectedFile) return;
    const effectiveLabel = label.trim() || "Consulta";
    upload.mutate({ file: selectedFile, label: effectiveLabel });
  };

  const handleStartPaste = () => {
    const trimmed = transcriptText.trim();
    if (trimmed.length < MIN_TRANSCRIPT_CHARS) {
      toast.error(`A transcrição precisa ter pelo menos ${MIN_TRANSCRIPT_CHARS} caracteres.`);
      return;
    }
    if (!label.trim()) {
      toast.error("Adicione um título para a consulta.");
      return;
    }
    analyzeTranscriptM.mutate({ label: label.trim(), transcriptText: trimmed });
  };

  const activeOp = inputMode === "upload" ? upload : analyzeTranscriptM;
  const activeStage = activeOp.stage;
  const activeError = activeOp.error;
  const activeData = activeOp.data;

  // Track job ID after upload OR paste-transcript analysis completes
  useEffect(() => {
    if (activeStage === "done" && activeData?.consultaId) {
      // Persist per-event consent immediately on success (LGPD audit trail).
      recordConsent.mutate({
        kind: inputMode === "upload" ? "audio_upload_v1" : "transcript_upload_v1",
        target_id: activeData.consultaId,
      });
      // Reset checkbox so next submission requires explicit re-affirmation.
      setAudioConsent(false);
      setTranscriptConsent(false);
      void consultasQuery.refetch().then((res) => {
        const target =
          res.data?.find((item) => item.id === activeData.consultaId) ?? null;

        if (!target) {
          toast.error("Análise concluída, mas não encontramos o resultado no histórico. Tente atualizar a página.");
          return;
        }

        if (target.aiJobId) {
          setLatestJobId(target.aiJobId);
        }

        if (activeData.duplicated) {
          toast.info("Esta consulta já tinha sido processada. Reabrimos a análise existente.");
        }

        void createNotification({
          type: target.jobStatus === "failed" ? "job_failed" : "job_done",
          title:
            target.jobStatus === "failed"
              ? "Análise de consulta falhou"
              : "Análise de consulta concluída",
          body: target.label ?? undefined,
          href: `/consultas/${target.id}`,
          metadata: { job_id: target.aiJobId },
        });

        setSuccessPreview(extractAnalysisPreview(target.analysisPayload));
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStage, activeData?.consultaId, activeData?.duplicated]);

  useEffect(() => {
    if (activeError) {
      toastError(activeError, "Não foi possível processar a consulta. Verifique sua conexão e tente novamente.");
    }
  }, [activeError]);

  function toggleSelection(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function handleBulkDelete() {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(ids.map((id) => deleteConsulta.mutateAsync(id)));
    const success = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - success;
    if (success > 0) toast.success(`${success} consulta(s) excluída(s).`);
    if (failed > 0) toast.error(`${failed} consulta(s) não puderam ser excluídas.`);
    setSelectedIds(new Set());
    setBulkDeleting(false);
  }


  const isUploading =
    activeStage !== "idle" && activeStage !== "done" && activeStage !== "error";

  const transcriptCharCount = transcriptText.trim().length;
  const transcriptValid = transcriptCharCount >= MIN_TRANSCRIPT_CHARS;
  // Paste mode skips Whisper, so transcription key is not required.
  const showKeyGate =
    inputMode === "upload" &&
    !transcriptionKey.isLoading &&
    !transcriptionKey.canTranscribe;

  return (
    <AppLayout>
      <div className="min-h-full bg-background p-4 sm:p-6 animate-fade-in">
        {/* Header */}
        <div className="mb-6 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <ChartLineUp weight="duotone" className="w-6 h-6 text-primary" aria-hidden="true" />
              <h1 className="text-2xl font-semibold text-foreground">Analisar Consulta</h1>
              <HelpButton
                title="Analisar Consulta"
                description="Transforme áudio de consulta de vendas em diagnóstico acionável."
              >
                <p>
                  Envie o áudio (até 200MB) ou cole a transcrição de uma consulta
                  realizada. A IA identifica <strong>etapas do método</strong>
                  {" "}cobertas, objeções não respondidas, momentos de quebra de
                  rapport e onde a venda foi ganha ou perdida.
                </p>
                <p>
                  <strong>Como usar:</strong> grave a consulta (com consentimento
                  LGPD do lead), suba o arquivo, marque o checkbox e clique em
                  "Iniciar Análise". Pode fechar a página — avisamos por
                  notificação.
                </p>
                <p className="text-muted-foreground">
                  Etapa do método: pós-consulta, para revisar e ajustar
                  abordagem antes da próxima call.
                </p>
              </HelpButton>
            </div>
            <p className="text-muted-foreground">
              Envie um áudio da consulta para transcrição e análise com IA
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="gap-2 shrink-0"
            onClick={() => navigate("/consultas/insights")}
          >
            <ChartBar className="w-4 h-4" aria-hidden="true" />
            <span className="hidden sm:inline">Insights agregados</span>
            <span className="sm:hidden">Insights</span>
          </Button>
        </div>

        {/* ── Input area (upload | paste transcript) ── */}
        <div className="space-y-4 mb-8">
          {showKeyGate && <TranscriptionKeyGate.Inline />}

          {activeStage === "idle" || activeStage === "error" ? (
            <Tabs
              value={inputMode}
              onValueChange={(v) => {
                setInputMode(v as "upload" | "paste");
                upload.reset();
                analyzeTranscriptM.reset();
                setSuccessPreview(null);
              }}
            >
              <TabsList className="grid w-full grid-cols-2 sm:w-auto sm:inline-flex">
                <TabsTrigger value="upload">Enviar áudio</TabsTrigger>
                <TabsTrigger value="paste">Colar transcrição</TabsTrigger>
              </TabsList>

              <TabsContent value="upload" className="space-y-4 mt-4">
                <div className="bg-card border border-border/50 rounded-xl p-6 space-y-4">
                  {/* LGPD audio consent gate (Sprint 0 Sec1 + UX rec b) */}
                  <ConsentGate
                    id="consent-audio-upload"
                    checked={audioConsent}
                    onCheckedChange={setAudioConsent}
                    disabled={isUploading || !transcriptionKey.canTranscribe}
                    label="Confirmo que o paciente/lead consentiu com a gravação e análise por IA deste áudio."
                    helper="Necessário a cada upload • Registramos data e usuário para conformidade LGPD."
                  />
                  <DropZone
                    onFile={handleFile}
                    disabled={
                      isUploading ||
                      !transcriptionKey.canTranscribe ||
                      !audioConsent
                    }
                    disabledReason={!audioConsent ? "Marque o consentimento acima para habilitar o envio." : undefined}
                  />
                </div>

                {selectedFile && (
                  <div className="rounded-lg border bg-card p-4 flex items-center gap-3">
                    <FileAudio
                      weight="duotone"
                      className="w-6 h-6 text-primary shrink-0"
                      aria-hidden="true"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatBytes(selectedFile.size)}
                      </p>
                    </div>
                    <button
                      type="button"
                      aria-label="Remover arquivo"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                      onClick={() => {
                        setSelectedFile(null);
                        upload.reset();
                      }}
                    >
                      <X className="w-4 h-4" aria-hidden="true" />
                    </button>
                  </div>
                )}

                {selectedFile && (
                  <div className="space-y-3">
                    <div>
                      <label
                        htmlFor="consulta-label"
                        className="text-sm font-medium text-foreground"
                      >
                        Título da consulta
                      </label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Use o nome do paciente para identificar mais fácil depois.
                      </p>
                      <input
                        id="consulta-label"
                        type="text"
                        value={label}
                        onChange={(e) => setLabel(e.target.value)}
                        className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Ex: João Silva — 25/04/2026"
                        maxLength={120}
                      />
                    </div>
                    <Button
                      className="gap-2 w-full sm:w-auto"
                      onClick={handleStartUpload}
                      disabled={isUploading || !label.trim() || !audioConsent}
                    >
                      <CloudArrowUp className="w-4 h-4" aria-hidden="true" />
                      Iniciar Análise
                    </Button>
                  </div>
                )}
              </TabsContent>

              <TabsContent value="paste" className="space-y-4 mt-4">
                <div className="bg-card border border-border/50 rounded-xl p-6 space-y-4">
                  {/* LGPD transcript consent gate */}
                  <ConsentGate
                    id="consent-transcript-upload"
                    checked={transcriptConsent}
                    onCheckedChange={setTranscriptConsent}
                    disabled={isUploading}
                    label="Confirmo que o paciente/lead consentiu com a análise por IA desta transcrição."
                    helper="Necessário a cada envio • Registramos data e usuário para conformidade LGPD."
                  />
                  <div>
                    <label
                      htmlFor="consulta-label-paste"
                      className="text-sm font-medium text-foreground"
                    >
                      Título da consulta
                    </label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Use o nome do paciente para identificar mais fácil depois.
                    </p>
                    <input
                      id="consulta-label-paste"
                      type="text"
                      value={label}
                      onChange={(e) => setLabel(e.target.value)}
                      className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="Ex: João Silva — 25/04/2026"
                      maxLength={120}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="consulta-transcript"
                      className="text-sm font-medium text-foreground"
                    >
                      Transcrição
                    </label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Cole aqui uma transcrição já pronta. A IA pula a etapa de transcrição
                      e analisa diretamente — sem custo de transcrição.
                    </p>
                    {/* A6-A11y — aria-describedby links the char-count helper
                        + aria-invalid surfaces the validation state to AT
                        (WCAG 1.3.1 + 3.3.1). A16 — pair color with a Warning
                        glyph so color-blind users get a non-color signal. */}
                    <textarea
                      id="consulta-transcript"
                      value={transcriptText}
                      onChange={(e) => setTranscriptText(e.target.value)}
                      rows={10}
                      aria-describedby="consulta-transcript-helper"
                      aria-invalid={transcriptCharCount > 0 && !transcriptValid}
                      className="mt-1 block w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary font-mono"
                      placeholder="Cole o texto completo da transcrição aqui..."
                    />
                    <p
                      id="consulta-transcript-helper"
                      className={cn(
                        "text-xs mt-1 inline-flex items-center gap-1",
                        transcriptValid ? "text-muted-foreground" : "text-destructive",
                      )}
                    >
                      {!transcriptValid && (
                        <Warning aria-hidden="true" className="w-3 h-3" />
                      )}
                      {transcriptCharCount} caracteres
                      {!transcriptValid && ` — mínimo ${MIN_TRANSCRIPT_CHARS}`}
                    </p>
                  </div>

                  <Button
                    className="gap-2 w-full sm:w-auto"
                    onClick={handleStartPaste}
                    disabled={
                      isUploading || !label.trim() || !transcriptValid || !transcriptConsent
                    }
                  >
                    <CloudArrowUp className="w-4 h-4" aria-hidden="true" />
                    Analisar transcrição
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          ) : null}

          {/* Progress while processing */}
          {isUploading && (
            <div className="rounded-xl border bg-card p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="font-medium text-foreground">
                  {inputMode === "paste" ? "Analisando transcrição…" : "Processando…"}
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={activeOp.abort}
                  className="gap-1 text-muted-foreground"
                >
                  <X className="w-4 h-4" aria-hidden="true" />
                  Cancelar
                </Button>
              </div>
              <ProgressStepper stage={activeStage} />
              {/* A18: ETA + leave-page-safe copy. */}
              <p className="text-xs text-muted-foreground mt-3">
                {inputMode === "paste"
                  ? "Geralmente 30-60s. Pode fechar a página — avisaremos por notificação."
                  : "Geralmente 1-2 min por 10 min de áudio. Pode fechar a página — avisaremos por notificação."}
              </p>
            </div>
          )}

          {/* Success preview */}
          {activeStage === "done" && (
            <div className="rounded-xl border border-green-600/30 bg-green-50 dark:bg-green-950/20 p-6 space-y-3">
              <div className="flex items-center gap-2 text-green-700 dark:text-green-400">
                <CheckCircle weight="fill" className="w-5 h-5" aria-hidden="true" />
                <p className="font-semibold">Análise concluída!</p>
              </div>
              {successPreview && (
                <p className="text-sm text-foreground line-clamp-3">{successPreview}…</p>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedFile(null);
                  setTranscriptText("");
                  upload.reset();
                  analyzeTranscriptM.reset();
                  setSuccessPreview(null);
                  const target = activeData?.consultaId
                    ? consultasQuery.data?.find((c) => c.id === activeData.consultaId)
                    : consultasQuery.data?.[0];
                  if (target) navigate(`/consultas/${target.id}`);
                }}
              >
                Abrir análise completa
              </Button>
            </div>
          )}

          {/* Error state */}
          {activeStage === "error" && activeError && (
            <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 flex items-start gap-3">
              <Warning
                weight="duotone"
                className="w-5 h-5 text-destructive mt-0.5 shrink-0"
                aria-hidden="true"
              />
              <div className="flex-1 space-y-2">
                <p className="text-sm font-medium text-destructive">Erro no processamento</p>
                <p className="text-sm text-muted-foreground">
                  {friendlyError(activeError, "Não foi possível processar. Verifique sua conexão e tente novamente.")}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  onClick={() => activeOp.reset()}
                >
                  <ArrowClockwise className="w-4 h-4" aria-hidden="true" />
                  Tentar novamente
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* ── Análises recentes ── */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-medium text-muted-foreground">
              Análises recentes
              {(consultasQuery.data?.length ?? 0) > 0 && (
                <Badge variant="outline" className="ml-2 text-xs">
                  {consultasQuery.data!.length}
                </Badge>
              )}
            </h2>
          </div>
          <BulkActionsBar
            totalCount={consultasQuery.data?.length ?? 0}
            selectedCount={selectedIds.size}
            allSelected={
              (consultasQuery.data?.length ?? 0) > 0 &&
              selectedIds.size === (consultasQuery.data?.length ?? 0)
            }
            selectionMode={selectionMode}
            deleting={bulkDeleting}
            entityPluralLabel="consultas"
            onToggleSelectionMode={(enabled) => {
              setSelectionMode(enabled);
              if (!enabled) setSelectedIds(new Set());
            }}
            onToggleSelectAll={(checked) => {
              if (!checked) {
                setSelectedIds(new Set());
                return;
              }
              setSelectedIds(new Set((consultasQuery.data ?? []).map((c) => c.id)));
            }}
            onClearSelection={() => setSelectedIds(new Set())}
            onConfirmDelete={handleBulkDelete}
          />

          {consultasQuery.isLoading && (
            <div className="flex items-center justify-center py-8 text-muted-foreground gap-2">
              <CircleNotch className="w-5 h-5 animate-spin" aria-hidden="true" />
              Carregando…
            </div>
          )}

          {consultasQuery.isError && (
            <div className="flex flex-col items-center justify-center py-8 gap-3 text-muted-foreground">
              <Warning weight="duotone" className="w-8 h-8" aria-hidden="true" />
              <p>Erro ao carregar histórico.</p>
              <Button variant="outline" size="sm" onClick={() => void consultasQuery.refetch()}>
                Tentar novamente
              </Button>
            </div>
          )}

          {consultasQuery.isSuccess && consultasQuery.data.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <p className="text-sm">Nenhuma análise ainda. Envie um áudio acima.</p>
            </div>
          )}

          {consultasQuery.isSuccess && consultasQuery.data.length > 0 && (
            <div className="space-y-2">
              {consultasQuery.data.map((c) => (
                <ConsultaRow
                  key={c.id}
                  consulta={c}
                  selectionMode={selectionMode}
                  selected={selectedIds.has(c.id)}
                  onToggleSelected={(checked) => toggleSelection(c.id, checked)}
                  disableActions={bulkDeleting}
                  onOpen={() => navigate(`/consultas/${c.id}`)}
                  onRename={async (newName) => {
                    try {
                      await renameConsulta.mutateAsync({ id: c.id, label: newName });
                      toast.success("Renomeado com sucesso.");
                    } catch (err) {
                      toastError(err, "Não foi possível renomear. Tente novamente.");
                      throw err;
                    }
                  }}
                  onDelete={async () => {
                    try {
                      await deleteConsulta.mutateAsync(c.id);
                      toast.success("Consulta excluída.");
                    } catch (err) {
                      toastError(err, "Não foi possível excluir. Tente novamente.");
                      throw err;
                    }
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
